import { createAsyncThunk } from "@reduxjs/toolkit";
import { baseUrl, headersDefault } from ".";

/**
 * Fetch featured videos (home page feed — one best video per category).
 * Params: { page, limit }
 */
export const getFeaturedVideos = createAsyncThunk(
  "videos/getFeaturedVideos",
  async ({ page = 1, limit = 15 } = {}) => {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("limit", limit);

    const response = await fetch(
      `${baseUrl}/api/v1/videos/featured?${params.toString()}`,
      { headers: headersDefault() }
    );
    if (!response.ok) throw new Error(response.statusText ?? "GenericError");
    const data = await response.json();
    return { ...data, page };
  }
);

/**
 * Fetch filtered, paginated video list.
 * Powers category pages, search, and "Preferiti".
 */
export const getVideosList = createAsyncThunk(
  "videos/getVideosList",
  async ({
    term,
    categories,
    parent_category,
    visibilities,
    tags,
    liked,
    order_by,
    page = 1,
    limit = 15,
  } = {}) => {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("limit", limit);
    if (term) params.append("term", term);
    if (order_by) params.append("order_by", order_by);
    // When set, backend returns any video whose category.id OR category.parent_id
    // matches — replaces the need to enumerate child subcategories client-side.
    if (parent_category) params.append("parent_category", parent_category);
    if (liked !== undefined && liked !== null)
      params.append("liked", liked ? 1 : 0);
    if (categories)
      categories.forEach((c) => params.append("categories[]", c));
    if (visibilities)
      visibilities.forEach((v) => params.append("visibilities[]", v));
    if (tags) tags.forEach((t) => params.append("tags[]", t));

    const response = await fetch(
      `${baseUrl}/api/v1/videos/list?${params.toString()}`,
      { headers: headersDefault() }
    );
    if (!response.ok) throw new Error(response.statusText ?? "GenericError");
    const data = await response.json();
    return { ...data, page };
  }
);

/**
 * Fetch videos for the VideoCategory page. Same endpoint as getVideosList
 * but writes to a dedicated slice (s.videos.categoryList) so navigating
 * between /multimedia and /multimedia/categoria/:id doesn't clobber state
 * between the two pages.
 */
export const getCategoryVideos = createAsyncThunk(
  "videos/getCategoryVideos",
  async ({
    term,
    categories,
    parent_category,
    visibilities,
    tags,
    liked,
    order_by,
    page = 1,
    limit = 15,
  } = {}) => {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("limit", limit);
    if (term) params.append("term", term);
    if (order_by) params.append("order_by", order_by);
    if (parent_category) params.append("parent_category", parent_category);
    if (liked !== undefined && liked !== null)
      params.append("liked", liked ? 1 : 0);
    if (categories)
      categories.forEach((c) => params.append("categories[]", c));
    if (visibilities)
      visibilities.forEach((v) => params.append("visibilities[]", v));
    if (tags) tags.forEach((t) => params.append("tags[]", t));

    const response = await fetch(
      `${baseUrl}/api/v1/videos/list?${params.toString()}`,
      { headers: headersDefault() }
    );
    if (!response.ok) throw new Error(response.statusText ?? "GenericError");
    const data = await response.json();
    return { ...data, page };
  }
);

/**
 * Fetch related videos for a given video (Video correlati slider).
 * Server-side ranked: bucket 1 (same direct subcategory), bucket 2 (shares ≥1 tag).
 * Not paginated — returns up to `limit` items in a flat { data: [...] } envelope.
 */
export const getRelatedVideos = createAsyncThunk(
  "videos/getRelatedVideos",
  async ({ id, limit = 20 } = {}) => {
    const params = new URLSearchParams();
    params.append("limit", limit);
    const response = await fetch(
      `${baseUrl}/api/v1/videos/${id}/related?${params.toString()}`,
      { headers: headersDefault() }
    );
    if (!response.ok) throw new Error(response.statusText ?? "GenericError");
    return await response.json();
  }
);

/**
 * Fetch videos under a parent category (Altri video feed on video detail page).
 * Backend returns videos whose category.id == parent OR category.parent_id == parent.
 * Paginated for infinite scroll. Separate from getVideosList so state doesn't
 * collide with Multimedia / VideoCategory / VideoPreferiti.
 */
export const getParentCategoryVideos = createAsyncThunk(
  "videos/getParentCategoryVideos",
  async ({
    parent_category,
    order_by = "recent",
    page = 1,
    limit = 15,
  } = {}) => {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("limit", limit);
    if (parent_category) params.append("parent_category", parent_category);
    if (order_by) params.append("order_by", order_by);
    const response = await fetch(
      `${baseUrl}/api/v1/videos/list?${params.toString()}`,
      { headers: headersDefault() }
    );
    if (!response.ok) throw new Error(response.statusText ?? "GenericError");
    const data = await response.json();
    return { ...data, page };
  }
);

/**
 * Fetch single video metadata for the detail page.
 */
export const fetchVideo = createAsyncThunk(
  "videos/fetchVideo",
  async ({ id }) => {
    const response = await fetch(`${baseUrl}/api/v1/videos/${id}/fetch`, {
      headers: headersDefault(),
    });
    if (!response.ok) throw new Error(response.statusText ?? "GenericError");
    return await response.json();
  }
);

/**
 * Generate Vimeo playback URL + increment view counter.
 * MUST be called before starting the Vimeo player.
 */
export const getVideoPlaybackUrl = createAsyncThunk(
  "videos/getVideoPlaybackUrl",
  async ({ id, postId = null }) => {
    // Opened from a Feed post: Premium Lite may watch that post's workout.
    const context = postId
      ? `?${encodeURIComponent("context[type]")}=post&${encodeURIComponent("context[id]")}=${encodeURIComponent(postId)}`
      : "";
    const response = await fetch(`${baseUrl}/api/v1/videos/${id}/view${context}`, {
      headers: headersDefault(),
    });
    if (!response.ok) throw new Error(response.statusText ?? "GenericError");
    return await response.json(); // { url: "https://vimeo.com/123456789" }
  }
);

/**
 * Fetch video categories tree for filter pills and subcategory navigation.
 */
export const getVideoCategories = createAsyncThunk(
  "videos/getVideoCategories",
  async ({
    term,
    parent_id,
    only_root_categories,
    include_subcategories,
    page = 1,
    limit = 100,
  } = {}) => {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("limit", limit);
    if (term) params.append("term", term);
    if (parent_id) params.append("parent_id", parent_id);
    if (only_root_categories !== undefined)
      params.append("only_root_categories", only_root_categories ? 1 : 0);
    if (include_subcategories !== undefined)
      params.append("include_subcategories", include_subcategories ? 1 : 0);

    const response = await fetch(
      `${baseUrl}/api/v1/videos/categories/list?${params.toString()}`,
      { headers: headersDefault() }
    );
    if (!response.ok) throw new Error(response.statusText ?? "GenericError");
    return await response.json();
  }
);

/**
 * Fetch subcategories for a specific parent category.
 * Uses a separate thunk and slice field so navigation from the main page
 * (root categories) to a category page (subcategories) doesn't flash stale data.
 */
export const getVideoSubcategories = createAsyncThunk(
  "videos/getVideoSubcategories",
  async ({ parent_id, page = 1, limit = 100 } = {}) => {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("limit", limit);
    if (parent_id) params.append("parent_id", parent_id);
    params.append("include_subcategories", 1);

    const response = await fetch(
      `${baseUrl}/api/v1/videos/categories/list?${params.toString()}`,
      { headers: headersDefault() }
    );
    if (!response.ok) throw new Error(response.statusText ?? "GenericError");
    return await response.json();
  }
);

/**
 * Toggle like on a video.
 * POST to like, DELETE to unlike.
 */
export const toggleVideoLike = createAsyncThunk(
  "videos/toggleVideoLike",
  async ({ id, liked }) => {
    const body = {
      id,
      reaction: "like",
      type: "app/video/infrastructure/models/video",
    };
    const method = liked ? "DELETE" : "POST";
    const response = await fetch(`${baseUrl}/api/v1/reactions/react`, {
      method,
      headers: headersDefault(),
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(response.statusText ?? "GenericError");
    return { id, liked: !liked };
  },
  {
    condition: (_, { getState }) => {
      return !getState().videos._likePending;
    },
  }
);

/**
 * Toggle the detail-page thumbs-up reaction without changing the saved/favorite
 * flag that powers video hearts and /multimedia/preferiti.
 */
export const toggleVideoReactionLike = createAsyncThunk(
  "videos/toggleVideoReactionLike",
  async ({ id, reactionLiked }) => {
    const body = {
      id,
      reaction: "like",
      type: "app/video/infrastructure/models/video",
    };
    const method = reactionLiked ? "DELETE" : "POST";
    const response = await fetch(`${baseUrl}/api/v1/reactions/react`, {
      method,
      headers: headersDefault(),
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(response.statusText ?? "GenericError");
    return { id, reactionLiked: !reactionLiked };
  },
  {
    condition: (_, { getState }) => {
      return !getState().videos._reactionLikePending;
    },
  }
);

/**
 * Toggle the per-user "completed" marker on a video (shown on the detail page
 * as Fatto / Provata / Visto by category). Uses the same reactions endpoint as
 * likes with reaction="completed", so each row stores the user + created_at.
 */
export const toggleVideoCompletion = createAsyncThunk(
  "videos/toggleVideoCompletion",
  async ({ id, completed }) => {
    const body = {
      id,
      reaction: "completed",
      type: "app/video/infrastructure/models/video",
    };
    const method = completed ? "DELETE" : "POST";
    const response = await fetch(`${baseUrl}/api/v1/reactions/react`, {
      method,
      headers: headersDefault(),
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(response.statusText ?? "GenericError");
    return { id, completed: !completed };
  },
  {
    condition: (_, { getState }) => {
      return !getState().videos._completionPending;
    },
  }
);

/**
 * Initiate purchase for paid content (video or dirette).
 * Returns Stripe clientSecret + publishableKey.
 */
export const purchaseProduct = createAsyncThunk(
  "videos/purchaseProduct",
  async ({ productId, section = "masterclub" }) => {
    const response = await fetch(
      `${baseUrl}/api/v1/shopping/direct-checkout`,
      {
        method: "POST",
        headers: headersDefault(),
        body: JSON.stringify({ section, product: productId }),
      }
    );
    if (!response.ok) throw new Error(response.statusText ?? "GenericError");
    return await response.json();
  }
);
